/**
 * 예약·표시 시각은 타임존 없는 벽시계 문자열만 사용합니다.
 * Date / toISOString / datetime-local 파싱을 거치면 브라우저·OS 타임존만큼 밀립니다.
 */

const WALL_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;

/** API 응답에서 camel/snake 모두 허용 */
export function pickWallClock(
  obj: { scheduledAt?: string | null; scheduled_at?: string | null } | null | undefined
): string | null {
  if (!obj) return null;
  const raw = obj.scheduledAt ?? obj.scheduled_at ?? null;
  if (raw == null || String(raw).trim() === '') return null;
  return normalizeWallClock(String(raw));
}

/** 어떤 형식이든 yyyy-MM-ddTHH:mm:ss 로 정규화. 실패 시 null */
export function normalizeWallClock(raw: string): string | null {
  const m = raw.trim().replace(' ', 'T').match(WALL_RE);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] ?? '00'}`;
}

/** datetime-local / date+time 입력용 yyyy-MM-ddTHH:mm */
export function toDateTimeLocalParts(raw?: string | null): {
  date: string;
  time: string;
} {
  const n = raw ? normalizeWallClock(raw) : null;
  if (!n) return { date: '', time: '' };
  return { date: n.slice(0, 10), time: n.slice(11, 16) };
}

/** date(yyyy-MM-dd) + time(HH:mm[.ss]) → 서버 전송용 벽시계 */
export function fromDateAndTime(date: string, time: string): string | null {
  const d = date.trim();
  const t = time.trim();
  if (!d && !t) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const tm = t.match(/^(\d{2}):(\d{2})/);
  if (!tm) return null;
  return `${d}T${tm[1]}:${tm[2]}:00`;
}

/** 보드·계정 표시용 — Date 객체 없이 문자열만 자르기 */
export function formatWallClockKo(raw?: string | null): string {
  const n = raw ? normalizeWallClock(raw) : null;
  if (!n) return '';
  const [, y, mo, d, h, mi] = n.match(WALL_RE)!;
  return `${y}. ${mo}. ${d}. ${h}:${mi}`;
}

/** 날짜만 (yyyy. mm. dd.) */
export function formatWallClockDateKo(raw?: string | null): string {
  const n = raw ? normalizeWallClock(raw) : null;
  if (!n) return '';
  const [, y, mo, d] = n.match(WALL_RE)!;
  return `${y}. ${mo}. ${d}.`;
}

/**
 * 정렬용 숫자 키. Asia/Seoul 벽시계를 로컬 컴포넌트로 해석해 비교합니다.
 * (브라우저 TZ와 무관하게 동일 문자열끼리는 동일 순서)
 */
export function wallClockSortKey(raw?: string | null): number {
  const n = raw ? normalizeWallClock(raw) : null;
  if (!n) return 0;
  const m = n.match(WALL_RE);
  if (!m) return 0;
  return Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    Number(m[6] ?? '0')
  );
}
